import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { Navigate } from 'react-router-dom';
import apiServices from '../../services/apiServices';
import { GetPortalAccess, PortalAccessResponse } from '../../services/dtos';
import { useAuth } from './auth';

// THE authorization source for the SPA (F5). /portal/access returns the
// user's EFFECTIVE roles from the server's portal role system — granted
// assignments plus derived roles like "Steward" (computed live from the
// job-steward relationship). One uniform list; the SPA neither knows nor
// cares which entries are granted vs derived. The old ServiceStack roles
// localStorage list (JsonServiceClient_roles) is gone. Everything here is
// COSMETIC — every endpoint re-checks access server-side, so hiding UI is
// UX, not security.

const PORTAL_ACCESS_KEY = 'PORTAL_ACCESS';

type Context = {
  access: PortalAccessResponse | null;
  loading: boolean;
  hasPortalRole(...roles: string[]): boolean;
  refresh(): Promise<void>;
};

const portalAccessContext = createContext<Context>(undefined!);

function PortalAccessProvider({ children }: React.PropsWithChildren<{}>) {
  const auth = useAuth();
  const [access, setAccess] = useState<PortalAccessResponse | null>(() => {
    try {
      const cached = localStorage.getItem(PORTAL_ACCESS_KEY);
      return cached ? (JSON.parse(cached) as PortalAccessResponse) : null;
    } catch {
      return null;
    }
  });
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiServices.client.get(new GetPortalAccess());
      setAccess(res);
      localStorage.setItem(PORTAL_ACCESS_KEY, JSON.stringify(res));
    } catch {
      // keep the last known value; server-side checks are authoritative anyway
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (auth.isLoggedIn) {
      refresh();
    } else {
      setAccess(null);
      localStorage.removeItem(PORTAL_ACCESS_KEY);
    }
  }, [auth.isLoggedIn, refresh]);

  const hasPortalRole = (...roles: string[]) => {
    if (!access?.roles) return false;
    if (access.roles.includes('Admin')) return true; // mirrors the server's superuser bypass
    return roles.some((r) => access.roles!.includes(r));
  };

  return (
    <portalAccessContext.Provider
      value={{ access, loading, hasPortalRole, refresh }}
    >
      {children}
    </portalAccessContext.Provider>
  );
}

function usePortalAccess() {
  const ctx = useContext(portalAccessContext);
  if (!ctx) {
    throw new Error(
      'PortalAccessProvider must be configured to be able to use usePortalAccess'
    );
  }
  return ctx;
}

// Route guard: authenticated AND holding one of the given portal roles
// (admins pass everything; 'Steward' means the computed flag). While the
// first /portal/access fetch is in flight we render nothing rather than
// bounce — cosmetic only, the server re-checks every request.
function RequirePortalRoles({
  children,
  roles,
  redirect,
}: React.PropsWithChildren<{ roles: string[]; redirect?: boolean }>) {
  const auth = useAuth();
  const { access, loading, hasPortalRole } = usePortalAccess();

  if (!auth.isLoggedIn) {
    return redirect ? <Navigate replace to="/login" /> : null;
  }
  if (!access && loading) return null;
  if (!hasPortalRole(...roles)) {
    return redirect ? <Navigate replace to="/" /> : null;
  }
  return <>{children}</>;
}

// Cosmetic gate for portal-role-specific UI. Renders nothing when the user
// lacks the role — never rely on this for protection; the server re-checks.
function RequirePortalRole({
  children,
  roles,
}: React.PropsWithChildren<{ roles: string[] }>) {
  const { hasPortalRole } = usePortalAccess();
  if (!hasPortalRole(...roles)) return null;
  return <>{children}</>;
}

export {
  PortalAccessProvider,
  usePortalAccess,
  RequirePortalRole,
  RequirePortalRoles,
};
